import { useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Button } from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import ConfirmModal from "components/Common/ConfirmModal"
import { useQuotation, useUpdateQuotationStatus } from "hooks/useQuotation"
import { useAllQuotationStatuses } from "hooks/useQuotationStatus"
import { downloadAndOpenFile } from "api/pdfApi"
import { formatReadableDate } from "utils/formatDate"
import { useToastMessage } from "hooks/useToastMessage"
import { useReturnPath } from "hooks/useReturnPath"
import QuotationPdfPreview from "./PdfPreview"
import SupportiveQuotations from "./Supportive/SupportiveQuotations"

const QuotationView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { identifier } = useParams()
  const { data, isLoading, refetch, isRefetching } = useQuotation(identifier)
  const { handleResponse, handleError } = useToastMessage()
  const { fromPath } = useReturnPath("/quotation-list")
  const [downloading, setDownloading] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)

  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
    useUpdateQuotationStatus()
  const { data: statusData } = useAllQuotationStatuses()
  const statusIdBySlug = useMemo(() => {
    const raw = statusData?.data
    const items = Array.isArray(raw) ? raw : (raw?.data ?? [])
    return Object.fromEntries(items.map(s => [s.slug, s.id]))
  }, [statusData])

  const quotation = data?.data ?? {}
  const pdfContent = quotation?.pdf_content ?? {}
  const isPending = quotation?.status?.slug === "pending"

  document.title = `${quotation?.qid ?? "Quotation"} | Aurobil Cars`

  const handleDownloadPdf = async () => {
    setDownloading(true)
    try {
      await downloadAndOpenFile({ url: `/quotations/${identifier}/pdf` })
    } catch (error) {
      handleError(error, "Failed to download quotation PDF.")
    } finally {
      setDownloading(false)
    }
  }

  const goEdit = () =>
    navigate(`/quotation-list/${quotation?.qid ?? identifier}/edit`, {
      state: { from: location },
    })

  const handleConfirmStatusChange = async () => {
    const slug = confirmModal
    setConfirmModal(null)
    const quotation_status_id = statusIdBySlug[slug]
    if (!quotation_status_id) return
    try {
      const response = await updateStatus({
        id: quotation.id,
        quotation_status_id,
      })
      handleResponse(
        response,
        `Quotation ${slug === "approved" ? "accepted" : "declined"} successfully.`,
        true
      )
    } catch (error) {
      handleError(error, `Failed to ${slug === "approved" ? "accept" : "decline"} quotation.`)
    }
  }

  return (
    <PageWrapper
      title="Quotation"
      breadcrumbItem={quotation?.qid ?? "Quotation View"}
    >
      <div className="quotation-view-toolbar d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div className="qv-meta">
          <h4 className="mb-0">{quotation?.qid ?? "Quotation"}</h4>
          <div className="text-muted small">
            {formatReadableDate(pdfContent?.quotation_date, "MMM d, y")} to{" "}
            {formatReadableDate(pdfContent?.valid_until, "MMM d, y")}
          </div>
        </div>
        <div className="qv-actions d-flex flex-wrap gap-2">
          <Button color="light" onClick={() => navigate(fromPath)}>
            <i className="mdi mdi-arrow-left me-1" />
            <span className="qv-btn-label">Back</span>
          </Button>
          <Button
            color="light"
            onClick={() => refetch()}
            disabled={isRefetching}
            title="Refresh"
          >
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          <Button
            color="info"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <i className="bx bx-loader bx-spin me-1" />
                <span className="qv-btn-label">Generating...</span>
              </>
            ) : (
              <>
                <i className="mdi mdi-download me-1" />
                <span className="qv-btn-label">PDF</span>
              </>
            )}
          </Button>
          <Button color="warning" onClick={goEdit}>
            <i className="mdi mdi-pencil-outline me-1" />
            <span className="qv-btn-label">Edit</span>
          </Button>
          {isPending && (
            <>
              <Button
                color="success"
                onClick={() => setConfirmModal("approved")}
              >
                <i className="mdi mdi-check-circle-outline me-1" />
                <span className="qv-btn-label">Accept</span>
              </Button>
              <Button
                color="danger"
                onClick={() => setConfirmModal("rejected")}
              >
                <i className="mdi mdi-close-circle-outline me-1" />
                <span className="qv-btn-label">Decline</span>
              </Button>
            </>
          )}
        </div>
      </div>

      <QuotationPdfPreview quotation={quotation} isLoading={isLoading} />

      {!quotation?.parent_quotation_id && quotation?.id && (
        <SupportiveQuotations quotation={quotation} onChanged={refetch} />
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        toggle={() => setConfirmModal(null)}
        title={confirmModal === "approved" ? "Accept Quotation?" : "Decline Quotation?"}
        message={
          confirmModal === "approved"
            ? "This will mark the quotation as accepted. Do you want to proceed?"
            : "This will mark the quotation as declined. Do you want to proceed?"
        }
        confirmLabel={confirmModal === "approved" ? "Accept" : "Decline"}
        icon={confirmModal === "approved" ? "mdi-check-circle-outline" : "mdi-close-circle-outline"}
        btnColor={confirmModal === "approved" ? "success" : "danger"}
        onConfirm={handleConfirmStatusChange}
        isConfirming={isUpdatingStatus}
      />
    </PageWrapper>
  )
}

export default QuotationView
